import { Link } from "react-router-dom";
import { ArrowUpRight, Mail } from "lucide-react";
import SectionReveal from "@/components/SectionReveal";

const TEAM = [
  { name: "Brett Rierson", role: "Founder & CEO", img: "/Images/Team/Brett.png", body: "INSEAD MBA; 35 years executive leadership in Africa/Asia; Founder, WFP Global Post-Harvest Centre." },
  { name: "Timothy Mucunguzi", role: "Founder · Director, Regulatory & Government Affairs", img: "/Images/Team/Timothy.png", body: "Leads regulatory strategy and government affairs, with experience across policy engagement, compliance, and international stakeholder relations." },
  { name: "Geoffrey Kisitu", role: "Director, Engineering", img: "/Images/Team/Geoffrey.png", body: "Former Head of Engineering at CiplaQCIL; veteran of multimillion-dollar sterile and oncology facility builds in East Africa." },
  { name: "Ambrose O'Donnell", role: "Director, Quality & Compliance", img: "/Images/Team/Ambrose.png", body: "15+ years in cleanroom implementation and EU-GMP validation (IQ/OQ/PQ) for international pharmaceutical manufacturing." },
];

const ADV_PHASE1 = [
  { title: "Chair, International Advisory Board", focus: "Strategic governance and global positioning." },
  { title: "Capital Markets & Strategic Finance Advisor", focus: "Capital structuring and institutional finance." },
  { title: "EU-GMP & Pharmaceutical Quality Advisor", focus: "GMP design, certification pathway, and quality systems." },
  { title: "International Commercial & Market Access Advisor", focus: "Importer relationships and channel access." },
  { title: "Controlled-Environment Cultivation Advisor", focus: "Indoor cultivation architecture and crop systems." },
  { title: "Post-Harvest, Processing & GMP Workflow Advisor", focus: "Drying, curing, packaging, QA release workflows." },
  { title: "Export Logistics & Controlled-Substance Compliance Advisor", focus: "Cold chain, customs, and controlled-substance logistics." },
];

const ADV_PHASE2 = [
  { title: "Uganda Health Policy & Regulatory Affairs Advisor", focus: "Domestic policy interface and licensing pathways." },
  { title: "Clinical, Pharma & Patient-Safety Advisor", focus: "Therapeutic positioning and patient-safety standards." },
  { title: "ESG, Sustainability & African Access Advisor", focus: "Environmental, social, and equitable-access frameworks." },
];

const NAMED_ADVISORS = [
  {
    name: "Dr. Véronique Bouchet",
    role: "Strategic Advisor",
    body: "Medical Doctor and strategic leader; 30+ years in pharma/biotech; former Director of Corporate Strategy and Head of Venture Strategy at AstraZeneca. Véronique supports Maisha Pharma's positioning with regulated-market stakeholders — pharmaceutical partners, clinical experts, investors, and institutional networks — ensuring the platform is framed as part of a responsible cannabinoid-medicine strategy grounded in patient safety, quality, and long-term therapeutic relevance.",
  },
  {
    name: "Franz Josef Sima",
    role: "Advisory Board · Controlled-Environment Cultivation & Systems Design",
    body: "20+ years in international GACP/GMP facility design and sustainable agricultural technology. Franz advises Maisha Pharma on controlled-environment cultivation, crop optimisation, and scalable production systems — helping the company design repeatable, efficient cultivation architecture capable of supporting pharmaceutical-grade consistency, cost discipline, and future expansion.",
  },
];

export default function Team() {
  return (
    <>
      <section className="pt-[72px] bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-16">
          <SectionReveal><div className="overline">Team & Advisors</div></SectionReveal>
          <SectionReveal delay={120}>
            <h1 className="mt-6 font-display font-light text-4xl md:text-5xl lg:text-[64px] leading-[1.04] tracking-editorial max-w-5xl">
              An institutional team — across pharmaceutical, cultivation, and capital disciplines.
            </h1>
          </SectionReveal>
          <SectionReveal delay={240}>
            <p className="mt-8 max-w-2xl text-ink2 text-lg leading-relaxed">
              Maisha Pharma is led by a founding team operating in Uganda and Europe, supported by a growing international advisory board across pharmaceutical quality, cultivation systems, regulation, and strategic finance.
            </p>
          </SectionReveal>
        </div>
      </section>

      {/* Core team */}
      <section className="bg-sand/40 border-y border-line/60">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <SectionReveal>
            <div className="overline">Founding Team</div>
            <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial">The people building Maisha Pharma.</h2>
          </SectionReveal>
          <div className="mt-16 space-y-px bg-line/60 border border-line/60">
            {/* Featured CEO */}
            {TEAM.slice(0, 1).map((m) => (
              <SectionReveal key={m.name}>
                <div data-testid="team-featured-ceo" className="group bg-bone p-8 md:p-12 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
                  <div className="md:col-span-4">
                    <div className="relative aspect-[4/5] max-w-xs overflow-hidden rounded-sm bg-bone border border-line/60">
                      <img src={m.img} alt={m.name} className="absolute inset-0 w-full h-full object-cover object-top transition-all duration-700 group-hover:grayscale-0 grayscale-[35%]" loading="lazy" />
                      <div className="absolute inset-x-0 bottom-0 h-1 bg-copper opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>
                  </div>
                  <div className="md:col-span-8 flex flex-col justify-center">
                    <div className="font-mono text-[11px] tracking-[0.2em] text-copper uppercase">T/01 · Office of the CEO</div>
                    <h3 className="mt-5 font-display font-light text-3xl md:text-4xl lg:text-5xl tracking-editorial leading-[1.05]">{m.name}</h3>
                    <div className="mt-3 text-sm md:text-base text-forest">{m.role}</div>
                    <p className="mt-7 text-base text-ink2 leading-relaxed max-w-2xl">{m.body}</p>
                    <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-[11px] tracking-[0.2em] uppercase text-ink2">
                      <span className="border-b border-copper pb-1">INSEAD MBA</span>
                      <span className="border-b border-line/60 pb-1">35 yrs · Africa &amp; Asia</span>
                      <span className="border-b border-line/60 pb-1">Founder · WFP Global Post-Harvest Centre</span>
                    </div>
                  </div>
                </div>
              </SectionReveal>
            ))}
            {/* Other leaders — 3-up row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-line/60">
              {TEAM.slice(1).map((m, i) => (
                <SectionReveal key={m.name} delay={i * 80}>
                  <div data-testid={`team-${i + 1}`} className="group bg-bone p-8 md:p-10 h-full">
                    <div className="relative aspect-[4/5] overflow-hidden rounded-sm bg-bone border border-line/60">
                      <img src={m.img} alt={m.name} className="absolute inset-0 w-full h-full object-cover object-top transition-all duration-700 group-hover:grayscale-0 grayscale-[35%]" loading="lazy" />
                      <div className="absolute inset-x-0 bottom-0 h-1 bg-copper opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                    </div>
                    <div className="mt-7 font-mono text-[11px] tracking-[0.2em] text-copper uppercase">T/0{i + 2}</div>
                    <h3 className="mt-4 font-display text-2xl tracking-tight leading-tight">{m.name}</h3>
                    <div className="mt-2 text-sm text-forest">{m.role}</div>
                    <p className="mt-5 text-sm text-ink2 leading-relaxed">{m.body}</p>
                  </div>
                </SectionReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Advisory board intro + named advisors */}
      <section className="bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <SectionReveal>
                <div className="overline">Advisory Board</div>
                <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial">Built across pharmaceutical, regulatory, and operational disciplines.</h2>
                <p className="mt-6 text-ink2 leading-relaxed max-w-md">
                  Maisha Pharma is building an advisory board across pharmaceutical quality, medicinal cannabis regulation, patient safety, controlled-environment cultivation, post-harvest processing, analytical testing, export logistics, and strategic finance. The board is intended to support the company's development as a regulated, export-led medicinal cannabis platform from Uganda.
                </p>
              </SectionReveal>
            </div>
            <div className="lg:col-span-7 space-y-px bg-line/60 border border-line/60">
              {NAMED_ADVISORS.map((a, i) => (
                <SectionReveal key={a.name} delay={i * 100}>
                  <div className="bg-bone p-8 md:p-10" data-testid={`named-advisor-${i}`}>
                    <div className="font-mono text-[11px] tracking-[0.2em] text-copper uppercase">A/0{i + 1}</div>
                    <h3 className="mt-6 font-display text-2xl tracking-tight">{a.name}</h3>
                    <div className="mt-2 text-sm text-forest">{a.role}</div>
                    <p className="mt-5 text-sm text-ink2 leading-relaxed">{a.body}</p>
                  </div>
                </SectionReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Advisory focus areas — Phase I + Phase II */}
      <section className="bg-sand/40 border-y border-line/60">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32">
          <SectionReveal>
            <div className="overline">Advisory Focus Areas</div>
            <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-5xl leading-[1.08] tracking-editorial max-w-3xl">Two phases. One regulated mandate.</h2>
          </SectionReveal>
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-px bg-line/60 border border-line/60">
            <SectionReveal>
              <div className="bg-bone p-8 md:p-10 h-full">
                <div className="font-mono text-[11px] tracking-[0.2em] text-copper uppercase">Phase I · Export</div>
                <h3 className="mt-4 font-display text-2xl">Regulated export platform.</h3>
                <ul className="mt-8 space-y-5">
                  {ADV_PHASE1.map((a) => (
                    <li key={a.title} className="grid grid-cols-12 gap-3 pb-4 border-b border-line/60">
                      <div className="col-span-12 md:col-span-7 font-display text-base text-ink">{a.title}</div>
                      <div className="col-span-12 md:col-span-5 text-sm text-ink2 leading-relaxed">{a.focus}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </SectionReveal>
            <SectionReveal delay={120}>
              <div className="bg-bone p-8 md:p-10 h-full">
                <div className="font-mono text-[11px] tracking-[0.2em] text-copper uppercase">Phase II · Domestic</div>
                <h3 className="mt-4 font-display text-2xl">Permitted domestic pathways.</h3>
                <ul className="mt-8 space-y-5">
                  {ADV_PHASE2.map((a) => (
                    <li key={a.title} className="grid grid-cols-12 gap-3 pb-4 border-b border-line/60">
                      <div className="col-span-12 md:col-span-7 font-display text-base text-ink">{a.title}</div>
                      <div className="col-span-12 md:col-span-5 text-sm text-ink2 leading-relaxed">{a.focus}</div>
                    </li>
                  ))}
                </ul>
                <p className="mt-8 text-sm text-ink2 leading-relaxed">
                  Phase II advisors support Maisha Pharma in domestic pathways pursued only where permitted under applicable Ugandan health, pharmaceutical, cosmetics, or consumer product regulations.
                </p>
              </div>
            </SectionReveal>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-bone">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-24 md:py-32 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <SectionReveal>
            <div className="overline">Advisor & Partner Interest</div>
            <h2 className="mt-6 font-display font-light text-3xl md:text-4xl lg:text-[52px] leading-[1.05] tracking-editorial max-w-3xl">If your expertise aligns with our focus areas, we'd value the conversation.</h2>
          </SectionReveal>
          <SectionReveal delay={120}>
            <Link to="/contact" data-testid="team-cta-contact" className="inline-flex items-center gap-2 bg-forest text-bone px-7 py-4 text-sm tracking-wide hover:bg-forest/85 transition-colors">
              <Mail size={16} /> Connect with the team <ArrowUpRight size={16} />
            </Link>
          </SectionReveal>
        </div>
      </section>
    </>
  );
}
